import React, { useState } from 'react';
import { Typography, Dialog, DialogTitle, DialogContent, DialogActions, Button } from '@material-ui/core';
import styled from 'styled-components';
import AddIcon from '@material-ui/icons/Add';
import { ModifyClinicProps } from '../../../types';
import { useQuery } from '@tanstack/react-query';
import { searchUserByEmail, User } from '../../../../../apis/users';
import useGetPrescribers from '../../hooks/useGetPrescribers';
import useCreatePrescriber from '../../hooks/useCreatePrescriber';
import useUpdatePrescriber from '../../hooks/useUpdatePrescriber';
import useRemovePrescriber from '../../hooks/useRemovePrescriber';
import PrescriberFormDialog from './ClinicPrescribers/PrescriberFormDialog';
import PrescribersTable from './ClinicPrescribers/PrescribersTable';
import { PrescriberFormData, initialFormData } from './ClinicPrescribers/types';

interface ClinicPrescribersTabProps {
  clinic: ModifyClinicProps['clinic'];
}

const ClinicPrescribersTab: React.FC<ClinicPrescribersTabProps> = ({ clinic }) => {
  const [searchInput, setSearchInput] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [isAddDialogOpen, setIsAddDialogOpen] = useState(false);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [userToDelete, setUserToDelete] = useState<User | null>(null);
  const [selectedUser, setSelectedUser] = useState<User | null>(null);
  const [formData, setFormData] = useState<PrescriberFormData>(initialFormData);
  const [dialogMode, setDialogMode] = useState<'add_new' | 'add_existing' | 'edit'>('add_new');

  const { prescribers, isLoading: isLoadingPrescribers } = useGetPrescribers(clinic._id);
  const { addPrescriber } = useCreatePrescriber(clinic._id);
  const { updatePrescriber } = useUpdatePrescriber(clinic._id);
  const { removePrescriber } = useRemovePrescriber(clinic._id);

  const { data: searchResults, isLoading: isSearching } = useQuery({
    queryKey: ['users', searchQuery],
    queryFn: () => searchUserByEmail(searchQuery),
    enabled: dialogMode === 'add_existing' && searchQuery.length > 0
  });

  const handleSearchKeyPress = (event: React.KeyboardEvent) => {
    if (event.key === 'Enter') {
      event.preventDefault();
      setSearchQuery(searchInput.trim());
    }
  };

  const handleAddNewPrescriber = () => {
    setDialogMode('add_new');
    setSelectedUser(null);
    setFormData(initialFormData);
    setIsAddDialogOpen(true);
  };

  const handleAddExistingPrescriber = () => {
    setDialogMode('add_existing');
    setSelectedUser(null);
    setFormData(initialFormData);
    setIsAddDialogOpen(true);
    setSearchInput('');
    setSearchQuery('');
  };

  const handleSubmit = async (submittedData: PrescriberFormData) => {
    if (dialogMode === 'edit' && selectedUser) {
      await updatePrescriber(selectedUser._id, submittedData);
      setFormData(submittedData);
      return;
    }

    if (dialogMode === 'add_existing' && selectedUser) {
      await updatePrescriber(selectedUser._id, submittedData);
    } else if (dialogMode === 'add_new') {
      await addPrescriber(submittedData);
    }

    setIsAddDialogOpen(false);
    setFormData(initialFormData);
    setSelectedUser(null);
  };

  const handleCancel = () => {
    setIsAddDialogOpen(false);
    setFormData(initialFormData);
    setSelectedUser(null);
  };

  const handleEditPrescriber = (user: User) => {
    setDialogMode('edit');
    setSelectedUser(user);
    setFormData({
      forename: user.forename,
      surname: user.surname,
      email: user.username,
      nmcNumber: user.nmc_number || '',
      gphcNumber: user.gphc_number || '',
      address: user.address || '',
      postcode: user.postcode || '',
      telephone: user.telephone || '',
      roles: user.roles,
      clinic_name: user.clinic_name || '',
      registration_body: user.registration_body || '',
      prescribing_protocols: user.prescribing_protocols || []
    });
    setIsAddDialogOpen(true);
  };

  const handleDeleteClick = async (user: User) => {
    setUserToDelete(user);
    setIsDeleteDialogOpen(true);
  };

  const handleConfirmDelete = async () => {
    if (userToDelete) {
      await removePrescriber(userToDelete._id);
      setIsDeleteDialogOpen(false);
      setUserToDelete(null);
    }
  };

  const handleCancelDelete = () => {
    setIsDeleteDialogOpen(false);
    setUserToDelete(null);
  };

  const handleRowClick = (rowData: any) => {
    if (!rowData || !prescribers) return;
    const user = prescribers.find(u => u._id === rowData.ID);
    if (user) {
      handleEditPrescriber(user);
    }
  };

  const handleUserSelect = (user: User) => {
    setSelectedUser(user);
    setFormData({
      forename: user.forename,
      surname: user.surname,
      email: user.username,
      nmcNumber: user.nmc_number || '',
      gphcNumber: user.gphc_number || '',
      address: user.address || '',
      postcode: user.postcode || '',
      telephone: user.telephone || '',
      roles: user.roles,
      clinic_name: user.clinic_name || '',
      registration_body: user.registration_body || '',
      prescribing_protocols: user.prescribing_protocols || []
    });
  };

  return (
    <>
      <Container>
        <h2>&nbsp;</h2>
        <ButtonContainer>
          <Button
            variant="contained"
            style={{ backgroundColor: '#2F9D6D', color: 'white' }}
            onClick={handleAddNewPrescriber}
          >
            <AddIcon style={{ marginRight: 8 }} />
            Add New Prescriber
          </Button>
          <Button
            variant="outlined"
            style={{ color: '#2F9D6D', borderColor: '#2F9D6D' }}
            onClick={handleAddExistingPrescriber}
          >
            <AddIcon style={{ marginRight: 8 }} />
            Add Existing User
          </Button>
        </ButtonContainer>

        <PrescribersTable
          prescribers={prescribers}
          isLoading={isLoadingPrescribers}
          onRowClick={handleRowClick}
          onDeleteClick={handleDeleteClick}
        />

        <PrescriberFormDialog
          open={isAddDialogOpen}
          onClose={handleCancel}
          onSubmit={handleSubmit}
          initialFormData={formData}
          dialogMode={dialogMode}
          searchInput={searchInput}
          searchQuery={searchInput}
          onSearchInputChange={setSearchInput}
          onSearchKeyPress={handleSearchKeyPress}
          searchResults={searchResults}
          isSearching={isSearching}
          onUserSelect={handleUserSelect}
          selectedUser={selectedUser}
        />

        <Dialog open={isDeleteDialogOpen} onClose={handleCancelDelete}>
          <DialogTitle>Remove Prescriber</DialogTitle>
          <DialogContent>
            <Typography>
              Are you sure you want to remove {userToDelete?.forename} {userToDelete?.surname} as a prescriber?
            </Typography>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCancelDelete}>Cancel</Button>
            <Button
              onClick={handleConfirmDelete}
              variant="contained"
              style={{ backgroundColor: '#2F9D6D', color: 'white' }}
            >
              Remove
            </Button>
          </DialogActions>
        </Dialog>
      </Container>
    </>
  );
};

const Container = styled.div`
  padding: 24px;
  h2 {
    font-size: 24px;
    margin: 0 0 24px 0;
    visibility: hidden;
    height: 29px;
  }
`;

const ButtonContainer = styled.div`
  display: flex;
  gap: 16px;
  margin-bottom: 24px;
`;

export default ClinicPrescribersTab;
